import { FormikProvider, useFormik } from 'formik';
import React, { useState, useRef, useEffect } from 'react';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { browserHistory, Link } from 'react-router';
import { pure } from 'recompose';
import TranslatableHelmet from '../../../../components/TranslatableHelmet';
import { PageHeader } from 'sema-ui-components';
import { Table } from 'components/Table';
import connectForm from 'utils/connectForm';

import { renderDate } from '../../utils';
import { IntermediaryTextField } from '../../../IntermediaryForms/components';
import {
  Block,
  Button,
  Checkbox,
  CheckboxGroup,
  FileInput,
  Heading,
  InlineAlert,
  MultiSelect,
  RadioButton,
  RadioButtonGroup
} from 'suomifi-ui-components';
import DatePicker from '../../../../components/DatePicker';
import styles from '../../../IntermediaryForms/partials/SubmitForm/styles.scss';
import ownStyles from './RegisterOidcEnvironmentView.scss';
import { getOidcSchema } from './schema';
import {
  acrKeys,
  FIELD,
  FIELD_VALUES,
  textFieldList,
  initialValues,
  defaultSelectedClaims,
  LEVEL_OF_DATA_PERMIT,
  claims
} from './constants';
import { resolveAcrValues, loadJsonFromInputFile, isValidDate } from './utils';
import { CustomFieldArray } from './CustomFieldArray';
import { BasicRadioButtonGroup } from './BasicRadioButtonGroup';

const RegisterOidcEnvironmentView = ({
  intl,
  identificationDecisions,
  postProviderJSON,
  sahaGuid,
  currentProvider,
  sendSuccessNotification,
  sendErrorNotification,
  params,
  routes,
  currentOrganization
}) => {
  const loc = 'identification_admin.environment.register_oidc';
  const i18n = local => intl.formatMessage({ id: local });
  const parseForm = (data, sahaGuid) => {
    return {
      status: 'draft',
      sahaguid: sahaGuid,
      env: data.env,
      environment: data.env,
      metadata: data,
      level_of_data_permit: data.level_of_permit
    };
  };

  const errorSummaryRef = useRef(null);

  const maybeScrollIntoErrorSummary = () => {
    errorSummaryRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  const onSubmitClick = async () => {
    const errors = await formik.validateForm();

    if (Object.keys(errors).length > 0) {
      maybeScrollIntoErrorSummary();
      return;
    }
  };

  const formik = useFormik({
    initialValues: initialValues,
    validateOnChange: false,
    validateOnBlur: false,
    validationSchema: getOidcSchema(i18n),
    onSubmit: values => {
      const dto = parseForm(values, sahaGuid);
      const res = postProviderJSON(currentProvider.id, dto);
      res.then(e => {
        // i.e. if POST is successful
        if (e.error === undefined) {
          browserHistory.push(
            `/${params.lang}/tunnistus/${currentProvider.nameFI}`
          );
          sendSuccessNotification(i18n(`${loc}.success`));
        } else if (
          // if there are backend validation errors
          e.payload?.message?.payload?.error?.errorMessages?.[0].error ===
          'validation_error'
        ) {
          const violations =
            e.payload.message.payload.error.errorMessages[0].violations;

          let backendErrors = [];

          for (const violation of violations) {
            // errors must be parsed into formik style errors so they can be
            // shown in ErrorSummary elem
            const fieldName = violation.field.split('.')[1];
            backendErrors.push({
              text: violation.message,
              inputId: fieldName,
              inputRef: fieldRefs.current[fieldName]
            });
            // formik must be informed, otherwise the error messages wont show
            formik.setFieldError(fieldName, violation.message);
          }
          setErrors(backendErrors);
          maybeScrollIntoErrorSummary();
        } else {
          sendErrorNotification(i18n(`${loc}.send_failure`));
        }
      });
    }
  });

  const [selectedItems, setSelectedItems] = useState(defaultSelectedClaims);
  const setSelectedItemsWrapper = async items => {
    /*
    The following array -> set -> array abomination exists because default selection can be and is overwritten,
    and we must be assured no dupes end up to neither the component nor the formik state.

    Note that form can be filled manually or via JSON.
    */
    const defaultValueInclusiveList = [
      ...new Set([...items, ...defaultSelectedClaims])
    ];

    setSelectedItems(defaultValueInclusiveList);
    await formik.setFieldValue(
      FIELD.SCOPE,
      defaultValueInclusiveList.map(x => x.labelText).join(' '),
      false
    );
    await formik.setFieldTouched(FIELD.SCOPE, true, false);
    await formik.validateField(FIELD.SCOPE);
  };

  const overrideErrorStatus = (formik, item) => {
    return formik.errors?.[item] && !Array.isArray(formik.errors?.[item]);
  };

  // form element fieldRefs need to be mapped for the error message summary element
  // fieldRefs are handled in a multitude of ways due to
  // different implementations (suomi.fi DS vs custom plus workarounds)
  const fieldRefs = useRef({});
  const setFieldRef = name => el => {
    fieldRefs.current[name] = el;
  };

  const [errors, setErrors] = useState([]);

  useEffect(() => {
    setErrors([]);
    const errorList = Object.keys(formik?.errors).map(error => {
      return {
        text: formik.errors[error],
        inputId: error,
        inputRef: fieldRefs.current[error]
      };
    });
    setErrors(errorList);
  }, [formik?.errors]);

  const decisionTableColumns = [
    {
      header: i18n(`${loc}.service_name_column`),
      property: 'decisionId'
    },
    {
      header: i18n(`${loc}.level_of_permit_column`),
      property: 'levelOfPermit'
    },
    {
      header: i18n(`${loc}.decision_date_column`),
      property: 'decisionAt'
    },
    {
      header: i18n(`${loc}.additional_info_column`),
      property: 'applicationLink'
    }
  ];

  const isTargetEnvProd = formik.values.env === 'production';

  const maybeTranslateForErrorSummary = item => {
    const translateItem = i => {
      return /^\S+$/.test(i) ? i18n(i) : i;
    };

    if (Array.isArray(item)) {
      return item.map(x => translateItem(x)).join(', ');
    }
    return translateItem(item);
  };

  return (
    <div>
      <TranslatableHelmet
        locale={params.lang}
        subTitle={i18n(`${loc}.title`)}
      />
      <PageHeader
        title={
          <span>
            <FormattedMessage id={`${loc}.title`} />
          </span>
        }
        routes={routes}
        params={params}
      />
      <FormikProvider value={formik}>
        <form onSubmit={formik.handleSubmit}>
          <Heading variant={'h3'}>
            <FormattedMessage id={`${loc}.env_title`} />
          </Heading>
          <Block my={'xl'}>
            <RadioButtonGroup
              variant="large"
              labelText={i18n(`${loc}.env_label`)}
              name={FIELD.ENV}
              ref={setFieldRef(FIELD.ENV)}
              groupStatus={
                formik.touched[FIELD.ENV] && formik.errors?.[FIELD.ENV]
                  ? 'error'
                  : null
              }
              groupStatusText={
                formik.touched[FIELD.ENV] && formik.errors?.[FIELD.ENV]
              }
              value={formik.values?.[FIELD.ENV]}
              onChange={async value => {
                await formik.setFieldValue(FIELD.ENV, value);
                formik.validateField(FIELD.ENV);

                if (value !== 'production') {
                  formik.setFieldValue(FIELD.NO_DECISION, null);
                  formik.setFieldValue(FIELD.DECISION_ID, null);
                  formik.setFieldValue(FIELD.JOURNAL_NUMBER, null);
                  formik.setFieldValue(FIELD.LEVEL_OF_PERMIT, null);
                }
              }}
            >
              <RadioButton value={FIELD_VALUES.ENV.TESTING}>
                {i18n(`${loc}.target_env_test_label`)}
              </RadioButton>
              <RadioButton value={FIELD_VALUES.ENV.PRODUCTION} disabled={false}>
                {i18n(`${loc}.target_env_prod_label`)}
              </RadioButton>
            </RadioButtonGroup>
          </Block>
          <Block>
            {formik.values?.[FIELD.ENV] === FIELD_VALUES.ENV.PRODUCTION && (
              <div>
                <hr />
                <Heading variant={'h3'}>
                  <FormattedMessage id={`${loc}.select_application_title`} />
                </Heading>
                <p>
                  <FormattedMessage id={`${loc}.select_application_label`} />
                </p>
                <Table
                  className="decisionTable"
                  data={
                    identificationDecisions
                      ? identificationDecisions.map(
                          ({ id, caseId, createdDate, formData }) => ({
                            decisionId: (
                              <RadioButtonGroup
                                labelMode={'hidden'}
                                variant="large"
                                name={FIELD.DECISION_ID}
                                ref={setFieldRef(FIELD.DECISION_ID)}
                                groupStatus={
                                  formik.errors?.[FIELD.DECISION_ID]
                                    ? 'error'
                                    : null
                                }
                                groupStatusText={
                                  formik.errors?.[FIELD.DECISION_ID]
                                }
                                value={formik.values?.[FIELD.DECISION_ID]}
                                onChange={value => {
                                  formik.setFieldValue(
                                    FIELD.DECISION_ID,
                                    value
                                  );
                                  formik.setFieldValue(
                                    FIELD.LEVEL_OF_PERMIT,
                                    LEVEL_OF_DATA_PERMIT.getValue(
                                      formData.clientServiceInformation
                                        .extentOfPersonalInformation
                                    )
                                  );
                                  formik.setFieldValue(FIELD.NO_DECISION, null);
                                  formik.setFieldValue(
                                    FIELD.JOURNAL_NUMBER,
                                    null
                                  );
                                }}
                              >
                                <RadioButton
                                  value={id}
                                  disabled={
                                    !isTargetEnvProd ||
                                    formik?.values?.[FIELD.NO_DECISION]
                                  }
                                >
                                  {
                                    formData.clientServiceInformation
                                      .services[0].serviceName
                                  }
                                </RadioButton>
                              </RadioButtonGroup>
                            ),
                            decisionAt: createdDate
                              ? renderDate(new Date(createdDate), true)
                              : '-',
                            serviceName:
                              formData.clientServiceInformation.services[0]
                                .serviceName,
                            levelOfPermit: (
                              <FormattedMessage
                                id={`${loc}.level_of_permit.${formData.clientServiceInformation.extentOfPersonalInformation}`}
                              />
                            ),
                            applicationLink: (
                              <Link
                                to={`/${params.lang}/omat-hakemukset/hakemus/${caseId}/tiedot`}
                              >
                                <FormattedMessage
                                  id={`${loc}.link_to_application`}
                                />
                              </Link>
                            )
                          })
                        )
                      : []
                  }
                  columns={decisionTableColumns}
                  rowKey="decisionId"
                />
                <CheckboxGroup>
                  <Checkbox
                    name={FIELD.NO_DECISION}
                    ref={setFieldRef}
                    onClick={newState => {
                      const checkValue = newState.checkboxState;
                      formik.setFieldValue(FIELD.NO_DECISION, checkValue);
                      if (checkValue) {
                        formik.setFieldValue(FIELD.DECISION_ID, null);
                        formik.setFieldValue(FIELD.LEVEL_OF_PERMIT, null);
                      }
                    }}
                    checked={formik?.values?.[FIELD.NO_DECISION] || false}
                    large
                    disabled={formik?.values?.[FIELD.ENV] !== 'production'}
                  >
                    <FormattedMessage id={`${loc}.no_decision_label`} />
                  </Checkbox>
                </CheckboxGroup>
                <Heading variant={'h3'} my={'l'}>
                  <FormattedMessage
                    id={`${loc}.fill_approved_application_title`}
                  />
                </Heading>
                <div
                  style={{
                    display: 'inline-block',
                    width: '25%',
                    verticalAlign: 'top',
                    marginTop: '-16px',
                    marginRight: '16px'
                  }}
                >
                  <IntermediaryTextField
                    name={FIELD.JOURNAL_NUMBER}
                    customRef={setFieldRef}
                    label={
                      'identification_admin.environment.register_oidc.journal_number_label'
                    }
                    width={'100%'}
                    disabled={formik.values?.[FIELD.NO_DECISION] !== true}
                  />
                </div>
                <div
                  style={{
                    display: 'inline-block',
                    width: '25%',
                    verticalAlign: 'top'
                  }}
                >
                  <DatePicker
                    id="approved_date"
                    language={params.lang}
                    label={i18n(
                      'identification_admin.environment.register_oidc.approved_date_label'
                    )}
                    name={FIELD.APPROVED_DATE}
                    ref={setFieldRef(FIELD.APPROVED_DATE)}
                    value={
                      isValidDate(formik.values?.[FIELD.APPROVED_DATE])
                        ? formik.values?.[FIELD.APPROVED_DATE]
                        : new Date()
                    }
                    onChange={async e => {
                      try {
                        await formik.setFieldValue(FIELD.APPROVED_DATE, e.date);
                      } catch (e) {
                        await formik.setFieldValue(FIELD.APPROVED_DATE, null);
                      } finally {
                        await formik.validateField(FIELD.APPROVED_DATE);
                      }
                    }}
                    disabled={formik.values?.[FIELD.NO_DECISION] !== true}
                  />
                </div>
              </div>
            )}
          </Block>
          <hr />
          <Block my={'xl'}>
            <FileInput
              labelText={i18n(
                'identification_admin.add_service.jsonImportLabel'
              )}
              inputButtonText={i18n(
                'identification_admin.add_service.jsonImportButton'
              )}
              dragAreaText={i18n(
                'identification_admin.add_service.jsonImportDragAreaHeader'
              )}
              removeFileText={i18n(
                'identification_admin.add_service.jsonImportRemoveFile'
              )}
              addedFileAriaText={i18n(
                'identification_admin.add_service.jsonImportAddedFile'
              )}
              hintText={i18n(
                'identification_admin.add_service.jsonImportDragAreaContent'
              )}
              onChange={e => {
                loadJsonFromInputFile(e, setSelectedItemsWrapper, formik);
              }}
            />
          </Block>
          <Block my={'xxl'}>
            <div ref={errorSummaryRef}>
              <Block>
                {errors.length > 0 && (
                  <InlineAlert
                    id={'errorSummary'}
                    labelText={i18n(
                      'intermediary.errorMessages.fillRequiredFields'
                    )}
                    status="warning"
                  >
                    <ul>
                      {errors.map(item => (
                        <li key={item.inputId}>
                          <button
                            type="button"
                            className={styles.errorListItemBtn}
                            onClick={() => {
                              item.inputRef.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start'
                              });
                            }}
                          >
                            {i18n(`${loc}.${item.inputId}_label`)}: &nbsp;
                            {maybeTranslateForErrorSummary(item.text)}
                          </button>
                        </li>
                      ))}
                    </ul>
                  </InlineAlert>
                )}
              </Block>
            </div>
          </Block>
          <hr />
          <Block my={'xxl'}>
            <Heading variant={'h2'} my={'l'}>
              <FormattedMessage id={`${loc}.organisation_title`} />
            </Heading>
            <Heading variant={'h4'} my={'xs'}>
              <FormattedMessage id={`${loc}.organisation_name`} />
            </Heading>
            <Block>
              <FormattedMessage id={`${loc}.lang_fi`} />:{' '}
              {currentOrganization.nameFi ||
                i18n('identification_admin.add_service.noInfo')}
            </Block>
            <Block>
              <FormattedMessage id={`${loc}.lang_en`} />:{' '}
              {currentOrganization.nameEn ||
                i18n('identification_admin.add_service.noInfo')}
            </Block>
            <Block>
              <FormattedMessage id={`${loc}.lang_sv`} />:{' '}
              {currentOrganization.nameSv ||
                i18n('identification_admin.add_service.noInfo')}
            </Block>
          </Block>
          <hr />
          <Block my={'xxxl'}>
            <Heading variant={'h2'} my={'l'}>
              <FormattedMessage
                id={`${loc}.identification_related_section_title`}
              />
              :
            </Heading>
            {textFieldList.map(field => {
              return (
                <IntermediaryTextField
                  name={field}
                  customRef={setFieldRef}
                  label={`${loc}.${field}_label`}
                  width={'51%'}
                  hintText={i18n(`${loc}.${field}_hint_text`)}
                />
              );
            })}
            <Block className={ownStyles.formWidth} my={'l'}>
              <BasicRadioButtonGroup
                i18n={i18n}
                loc={loc}
                name={FIELD.VERIFICATION_REQ}
                setFieldRef={setFieldRef}
                formik={formik}
                isArray={false}
                labels={true}
                translations={true}
                hideGroupHint={true}
              />
            </Block>
            <CustomFieldArray
              name={FIELD.CONTACT_EMAILS}
              formik={formik}
              customRef={setFieldRef}
              i18n={i18n}
              loc={loc}
              overrideErrorStatus={overrideErrorStatus}
            />
          </Block>
          <hr />
          <Block my={'l'}>
            <Heading variant={'h2'} my={'xl'}>
              <FormattedMessage
                id={`${loc}.oidc_integration_related_section_title`}
              />
            </Heading>
            <Heading variant={'h3'}>
              <FormattedMessage id={`${loc}.general_settings`} />
            </Heading>
            <Block my={'l'}>
              <IntermediaryTextField
                name={FIELD.CLIENT_ID}
                customRef={setFieldRef}
                label={`${loc}.client_id_label`}
                width={'51%'}
                hintText={i18n(`${loc}.client_id_hint_text`)}
              />
            </Block>
            <Block my={'l'}>
              <CheckboxGroup
                forwardedRef={setFieldRef(FIELD.DEFAULT_ACR_VALUES)}
                labelText={i18n(`${loc}.default_acr_values_label`)}
                groupHintText={i18n(`${loc}.default_acr_values_hint_text`)}
                groupStatus={
                  formik.touched[FIELD.DEFAULT_ACR_VALUES] &&
                  formik.errors?.[FIELD.DEFAULT_ACR_VALUES]
                    ? 'error'
                    : null
                }
                groupStatusText={
                  formik.touched[FIELD.DEFAULT_ACR_VALUES] &&
                  formik.errors?.[FIELD.DEFAULT_ACR_VALUES]
                }
              >
                {Object.keys(acrKeys).map((item, index) => {
                  return (
                    <Checkbox
                      name={item}
                      key={index}
                      hintText={acrKeys[item].join(', ')}
                      onClick={async newState => {
                        await resolveAcrValues(
                          item,
                          newState.checkboxState,
                          formik
                        );
                        formik.setFieldTouched(
                          FIELD.DEFAULT_ACR_VALUES,
                          true,
                          false
                        );
                        if (
                          !newState.checkboxState ||
                          formik.errors?.[FIELD.DEFAULT_ACR_VALUES]
                        ) {
                          formik.validateField(FIELD.DEFAULT_ACR_VALUES);
                        }
                      }}
                      checked={
                        formik?.values?._default_acr_values?.[item] || ''
                      }
                    >
                      <FormattedMessage
                        id={`${loc}.default_acr_values.${item}_label`}
                      />
                    </Checkbox>
                  );
                })}
              </CheckboxGroup>
            </Block>
            <Block className={ownStyles.formWidth} my={'l'}>
              <div
                ref={
                  setFieldRef(
                    FIELD.SCOPE
                  ) /* MultiSelect forwardedRef has a bug */
                }
              >
                <MultiSelect
                  labelText={i18n(`${loc}.scope_label`)}
                  hintText={i18n(`${loc}.scope_hint_text`)}
                  fullWidth={true}
                  items={claims}
                  chipListVisible
                  ariaChipActionLabel={i18n(`${loc}.remove_selection`)}
                  removeAllButtonLabel={i18n(`${loc}.remove_all_selections`)}
                  visualPlaceholder={i18n(`${loc}.scope_placeholder`)}
                  noItemsText={i18n(`${loc}.scope_no_items`)}
                  ariaSelectedAmountText={i18n(`${loc}.scope_select_count`)}
                  ariaOptionsAvailableText={i18n(`${loc}.scope_option_count`)}
                  ariaOptionChipRemovedText={i18n(
                    `${loc}.scope_removed_message`
                  )}
                  selectedItems={selectedItems}
                  status={
                    formik.touched[FIELD.SCOPE] && formik.errors?.[FIELD.SCOPE]
                      ? 'error'
                      : 'default'
                  }
                  statusText={
                    formik.touched[FIELD.SCOPE]
                      ? formik.errors?.[FIELD.SCOPE]
                      : ''
                  }
                  onItemSelectionsChange={async newSelectedItems => {
                    setSelectedItemsWrapper(newSelectedItems);
                  }}
                />
              </div>
            </Block>
            <Heading variant={'h3'} my={'xxl'}>
              <FormattedMessage id={`${loc}.open_id_section_title`} />
            </Heading>
            <Block my={'l'}>
              <IntermediaryTextField
                name={FIELD.JWKS_URI}
                customRef={setFieldRef}
                label={`${loc}.jwks_uri_label`}
                width={'51%'}
                hintText={i18n(`${loc}.jwks_uri_hint_text`)}
              />
            </Block>
            <Block my={'l'}>
              <CustomFieldArray
                name={FIELD.REDIRECT_URIS}
                customRef={setFieldRef}
                formik={formik}
                i18n={i18n}
                loc={loc}
                overrideErrorStatus={overrideErrorStatus}
              />
            </Block>
            <Block className={ownStyles.formWidth} my={'l'}>
              <BasicRadioButtonGroup
                i18n={i18n}
                loc={loc}
                name={FIELD.RESPONSE_TYPES}
                setFieldRef={setFieldRef}
                formik={formik}
                isArray={true}
              />
            </Block>
            <Block my={'l'}>
              <CustomFieldArray
                name={FIELD.POST_LOGOUT_REDIRECT}
                customRef={setFieldRef}
                formik={formik}
                i18n={i18n}
                loc={loc}
                overrideErrorStatus={overrideErrorStatus}
              />
            </Block>
            <Block className={ownStyles.formWidth} my={'l'}>
              <IntermediaryTextField
                name={FIELD.FRONTCHANNEL_LOGOUT_URI}
                customRef={setFieldRef}
                label={`${loc}.frontchannel_logout_uri_label`}
                hintText={i18n(`${loc}.frontchannel_logout_uri_hint_text`)}
              />
            </Block>
            <Block className={ownStyles.formWidth} my={'l'}>
              <BasicRadioButtonGroup
                i18n={i18n}
                loc={loc}
                name={FIELD.FRONTCHANNEL_LOGOUT}
                setFieldRef={setFieldRef}
                formik={formik}
                isArray={false}
                translations={true}
              />
            </Block>
            <Heading variant={'h2'} hy={'xxxl'}>
              <FormattedMessage id={`${loc}.token_api_section_title`} />
            </Heading>
            <Block className={ownStyles.formWidth} my={'l'}>
              <BasicRadioButtonGroup
                i18n={i18n}
                loc={loc}
                name={FIELD.GRANT_TYPES}
                setFieldRef={setFieldRef}
                formik={formik}
                isArray={true}
              />
            </Block>
            <Block my={'l'}>
              <BasicRadioButtonGroup
                i18n={i18n}
                loc={loc}
                name={FIELD.ID_TOKEN_ENC_RES_ALG}
                setFieldRef={setFieldRef}
                formik={formik}
                isArray={false}
              />
            </Block>
            <Block my={'l'}>
              <BasicRadioButtonGroup
                i18n={i18n}
                loc={loc}
                name={FIELD.TOKEN_ENC_RES_ENC}
                setFieldRef={setFieldRef}
                formik={formik}
                isArray={false}
              />
            </Block>
            <Block my={'l'}>
              <BasicRadioButtonGroup
                i18n={i18n}
                loc={loc}
                name={FIELD.TOKEN_SIGN_RES_ALG}
                setFieldRef={setFieldRef}
                formik={formik}
                isArray={false}
              />
            </Block>
            <Block className={ownStyles.formWidth} my={'l'}>
              <BasicRadioButtonGroup
                i18n={i18n}
                loc={loc}
                name={FIELD.TOKEN_AUTH_METHOD}
                setFieldRef={setFieldRef}
                formik={formik}
                isArray={false}
              />
            </Block>
          </Block>
          <hr />
          <Block my={'s'}>
            <Button type="submit" onClick={onSubmitClick}>
              <FormattedMessage id={'general.form.submit'} />
            </Button>
          </Block>
        </form>
      </FormikProvider>
    </div>
  );
};

export default injectIntl(
  pure(
    connectForm(RegisterOidcEnvironmentView, {
      form: 'registerOidcEnvironmentForm',
      destroyOnUnmount: true,
      mapStateToProps: () => state => ({})
    })
  )
);
